import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Footer from '../../components/Footer';

const CollectionPage = () => {
  const { slug } = useParams();
  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [sort, setSort] = useState('newest');
  const [loading, setLoading] = useState(true);
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  
  // Product placeholder image
  const productPlaceholder = '/product-placeholder.jpg';

  const load = async (p=1) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/shop/collections/${slug}?page=${p}&limit=12`);
      if (!res.ok) throw new Error('Failed to load collection');
      const data = await res.json();
      setCollection(data.collection);
      setProducts(data.products || []);
      setPage(data.page || 1);
      setPages(data.pages || 1);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, [slug]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <header className="bg-white dark:bg-gray-800/80 dark:bg-gray-800/80 backdrop-blur-md shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <h1 className="text-2xl font-black">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-black">TrainerList</span>
              </h1>
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/shop" className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800 font-bold">Shop</Link>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {collection && (
          <div className="mb-6">
            <h1 className="text-3xl font-black">{collection.title}</h1>
            {collection.description && <p className="text-gray-600 dark:text-gray-400 mt-2">{collection.description}</p>}
          </div>
        )}

        {/* Grid */}
        {loading && <p>Loading...</p>}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <Card key={product.id} className="overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105 border-2 border-transparent hover:border-red-300">
                <CardContent className="p-0">
                  <div className="relative bg-white dark:bg-gray-700 flex items-center justify-center h-48">
                    <img 
                      src={(product.media && product.media[0]) || productPlaceholder} 
                      alt={product.title} 
                      className="w-full h-full object-contain p-4"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = productPlaceholder;
                      }}
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-2 line-clamp-2">{product.title}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-black text-red-600">${product.min_price?.toFixed(2) || '0.00'} <span className="text-sm font-normal text-gray-500">CAD</span></span>
                      <Link to={`/shop/products/${product.slug}`} className="text-red-600 hover:text-red-800 font-semibold">View →</Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" disabled={page<=1} onClick={()=>load(page-1)}>Prev</Button>
          <span className="text-sm text-gray-600">Page {page} of {pages}</span>
          <Button variant="outline" disabled={page>=pages} onClick={()=>load(page+1)}>Next</Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CollectionPage;
